import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import SearchInput from 'tio-ui/components/search-input';
import {
  PhGraduationCap,
  PhChalkboardTeacher,
  PhChatCircleDots,
  PhCalendarCheck,
  PhSparkle,
  PhArrowRight,
  PhLightbulb,
  PhQuestion,
  PhMoney,
  PhHandshake,
  PhShieldCheck,
  PhClock,
  PhVideo,
  PhEnvelope,
  PhUsers,
} from 'ember-phosphor-icons';
import PageHero from 'tio-ui/components/page-hero';
import SectionHeader from 'tio-ui/components/section-header';
import Callout from 'tio-ui/components/callout';
import CoachCard from 'tio-employee/components/reimagined/coach-card';
import type { Coach } from 'tio-employee/components/reimagined/coach-card';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import FAQAccordion from 'tio-ui/components/faq-accordion';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';

interface FAQItemExtended {
  question: string;
  answer: string;
  category: string;
}

interface TopicArea {
  id: string;
  name: string;
  icon: unknown;
  description: string;
}

export default class ReimaginedCoachesPage extends Component {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked searchQuery = '';
  @tracked selectedSpecialty: string | null = null;

  /**
   * Demo coaches data - would come from API in production
   */
  get coaches(): Coach[] {
    return [
      {
        id: '1',
        name: 'Sarah Mitchell',
        title: 'Senior Student Loan Advisor',
        specialties: ['PSLF', 'IDR Plans', 'Loan Forgiveness'],
        rating: 4.9,
        reviewCount: 312,
        yearsExperience: 8,
        credentials: ['CFP®', 'CSLP®', 'AFC®'],
        availableSlots: 12,
        bio: "Sarah specializes in Public Service Loan Forgiveness and income-driven repayment strategies. She's helped over 1,000 borrowers navigate their path to loan forgiveness.",
      },
      {
        id: '2',
        name: 'Marcus Johnson',
        title: 'Financial Wellness Coach',
        specialties: ['Refinancing', 'Budgeting', 'Debt Strategy'],
        rating: 4.8,
        reviewCount: 245,
        yearsExperience: 6,
        credentials: ['AFC®', 'CSLP®'],
        availableSlots: 8,
        bio: 'Marcus focuses on holistic financial wellness, helping borrowers balance student loan repayment with other financial goals like saving and investing.',
      },
      {
        id: '3',
        name: 'Dr. Emily Chen',
        title: 'Healthcare Loan Specialist',
        specialties: ['Medical Loans', 'NHSC Programs', 'Residency Planning'],
        rating: 4.9,
        reviewCount: 189,
        yearsExperience: 10,
        credentials: ['PhD', 'CSLP®', 'CFP®'],
        availableSlots: 5,
        bio: 'Emily specializes in helping healthcare professionals manage their substantial student debt through specialized programs and strategic planning.',
      },
      {
        id: '4',
        name: 'James Rodriguez',
        title: 'Teacher Loan Expert',
        specialties: ['Teacher Forgiveness', 'State Programs', 'Public Service'],
        rating: 4.7,
        reviewCount: 156,
        yearsExperience: 5,
        credentials: ['CSLP®', 'MEd'],
        availableSlots: 15,
        bio: 'James is a former teacher turned student loan advisor, bringing firsthand experience to help educators maximize their loan forgiveness benefits.',
      },
    ];
  }

  get topicAreas(): TopicArea[] {
    return [
      {
        id: 'forgiveness',
        name: 'Loan Forgiveness',
        icon: PhShieldCheck,
        description: 'PSLF, Teacher Forgiveness, IDR Forgiveness',
      },
      {
        id: 'repayment',
        name: 'Repayment Strategy',
        icon: PhMoney,
        description: 'Plans, refinancing, accelerated payoff',
      },
      {
        id: 'employer',
        name: 'Employer Benefits',
        icon: PhHandshake,
        description: 'SLR, 127 benefits, matching programs',
      },
      {
        id: 'career',
        name: 'Career Planning',
        icon: PhGraduationCap,
        description: 'Career moves, salary negotiation, upskilling',
      },
    ];
  }

  get faqItems(): FAQItemExtended[] {
    return [
      {
        question: 'What can I discuss with a loan coach?',
        answer:
          'Our coaches can help with any student loan topic including repayment strategies, loan forgiveness programs (PSLF, Teacher Forgiveness, IDR), refinancing options, employer benefits, budgeting for loan payments, and understanding your loan terms. They cannot provide legal or tax advice.',
        category: 'general',
      },
      {
        question: 'How long are coaching sessions?',
        answer:
          'Standard sessions are 30 minutes, which is enough time to address most questions. For complex situations like PSLF planning or major financial decisions, we recommend booking a 60-minute deep-dive session.',
        category: 'scheduling',
      },
      {
        question: "Is coaching included with my employer's benefit?",
        answer:
          "Yes! Coaching sessions are fully covered as part of your employer's student loan benefit package. You can book unlimited sessions at no additional cost.",
        category: 'general',
      },
      {
        question: 'How do I prepare for my session?',
        answer:
          "Before your session, gather your loan information from StudentAid.gov and note your specific questions. Having your loan servicer information and current repayment plan details handy will help make the session more productive. Don't worry if you don't have everything—your coach can guide you.",
        category: 'preparation',
      },
      {
        question: 'Can I get help with PSLF certification?',
        answer:
          'Absolutely! Our PSLF specialists can walk you through the entire process, help you understand eligibility requirements, review your employment certification form, and create a strategy to maximize your qualifying payments.',
        category: 'pslf',
      },
    ];
  }

  get sessionTypes() {
    return [
      {
        id: 'video',
        icon: PhVideo,
        name: 'Video Call',
        description: '30 or 60 min face-to-face session',
        popular: true,
      },
      {
        id: 'phone',
        icon: PhClock,
        name: 'Phone Call',
        description: 'Quick 15-30 min check-in',
        popular: false,
      },
      {
        id: 'email',
        icon: PhEnvelope,
        name: 'Email Q&A',
        description: 'Detailed written response within 48hrs',
        popular: false,
      },
    ];
  }

  get filteredCoaches() {
    let coaches = this.coaches;

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      coaches = coaches.filter(
        (coach) =>
          coach.name.toLowerCase().includes(query) ||
          coach.specialties.some((s) => s.toLowerCase().includes(query)) ||
          coach.credentials.some((c) => c.toLowerCase().includes(query))
      );
    }

    if (this.selectedSpecialty) {
      coaches = coaches.filter((coach) =>
        coach.specialties.some((s) =>
          s.toLowerCase().includes(this.selectedSpecialty!.toLowerCase())
        )
      );
    }

    return coaches;
  }

  get allSpecialties() {
    const specialties = new Set<string>();
    this.coaches.forEach((coach) => {
      coach.specialties.forEach((s) => specialties.add(s));
    });
    return Array.from(specialties).sort();
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get coachDataSource() {
    // Coach data would typically be static/demo until real coach system built
    return this.isDemoMode ? 'demo' : 'placeholder';
  }

  setSearch = (value: string) => {
    this.searchQuery = value;
  };

  selectSpecialty = (specialty: string | null) => {
    this.selectedSpecialty = this.selectedSpecialty === specialty ? null : specialty;
  };

  scheduleWithCoach = (_coach: Coach) => {
    // Would open scheduling modal or navigate to scheduler
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack @collapsed={{true}}>
      {{! Hero Section }}
      <PageHero
        @icon={{PhChalkboardTeacher}}
        @title="Student Loan Coaches"
        @subtitle="Get personalized guidance from certified financial experts"
        @gradient="brand"
        role="banner"
      >
        <:content>
          <div class="tio-wrap mt-4">
            <div class="tio-row-tight tio-fg-on-brand">
              <PhUsers @size={{20}} />
              <span>4 Expert Coaches</span>
            </div>
            <div class="tio-row-tight tio-fg-on-brand">
              <PhCalendarCheck @size={{20}} />
              <span>40+ Slots Available</span>
            </div>
            <div class="tio-row-tight tio-fg-on-brand">
              <PhHandshake @size={{20}} />
              <span>Free with Your Benefit</span>
            </div>
          </div>
        </:content>
        <:actions>
          <Button @intent="primary" class="tio-bg-surface tio-fg-brand hover:tio-bg-brand-wash">
            <PhCalendarCheck @size={{20}} class="mr-2" />
            Schedule a Session
          </Button>
        </:actions>
      </PageHero>

      {{! AI Chat Teaser }}
      <Callout
        @intent="brand"
        @gradient={{true}}
        @iconBold={{true}}
        @overlap={{true}}
        @align="center"
        @title="Try Our AI Loan Advisor"
      >
        <:icon><PhSparkle @size={{28}} /></:icon>
        <:default>
          <p class="tio-fg-brand">
            Get instant answers to common student loan questions 24/7. Our AI can help with basic
            questions while you wait for your coaching session.
          </p>
        </:default>
        <:action>
          <Button @intent="primary" @appearance="outlined" class="shrink-0">
            <PhChatCircleDots @size={{20}} class="mr-2" />
            Chat Now
          </Button>
        </:action>
      </Callout>

      {{! Topic Areas }}
      <div class="px-6 py-8">
        <SectionHeader
          @icon={{PhLightbulb}}
          @title="What would you like help with?"
          @subtitle="Select a topic to find coaches who specialize in your needs"
          @iconClass="tio-fg-warning"
        />

        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {{#each this.topicAreas as |topic|}}
            {{#let topic.icon as |TopicIcon|}}
              <button
                type="button"
                class="tio-option-card
                  {{if (isEqual this.selectedSpecialty topic.name) 'tio-option-card--selected'}}"
                aria-pressed={{if (isEqual this.selectedSpecialty topic.name) "true" "false"}}
                {{on "click" (fn this.selectSpecialty topic.name)}}
              >
                <div class="tio-row mb-2">
                  {{! @glint-expect-error: dynamic icon }}
                  <TopicIcon @size={{24}} class="tio-fg-brand" />
                  <Text @weight="semibold">
                    {{topic.name}}
                  </Text>
                </div>
                <Text @size="xs" class="tio-fg-muted">
                  {{topic.description}}
                </Text>
              </button>
            {{/let}}
          {{/each}}
        </div>
      </div>

      {{! Meet Our Coaches Section }}
      <div class="px-6 py-8 tio-bg-sunken dark:bg-gray-900/50">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <SectionHeader
            @icon={{PhUsers}}
            @title="Meet Our Coaches"
            @subtitle="Certified experts ready to help you succeed"
            @iconClass="tio-fg-brand"
            @marginClass="mb-0"
          />

          <div class="tio-row">
            <SearchInput
              @value={{this.searchQuery}}
              @onChange={{this.setSearch}}
              @onSearch={{this.setSearch}}
              @placeholder="Search by name or specialty..."
              @label="Search coaches"
              class="w-64"
            />
          </div>
        </div>

        {{! Specialty Filter Chips }}
        <div class="tio-wrap-tight mb-6">
          <Chip
            class="cursor-pointer
              {{unless this.selectedSpecialty 'tio-bg-brand-accent text-white' 'tio-bg-surface'}}"
            {{on "click" (fn this.selectSpecialty null)}}
          >
            All
          </Chip>
          {{#each this.allSpecialties as |specialty|}}
            <Chip
              class="cursor-pointer
                {{if
                  (isEqual this.selectedSpecialty specialty)
                  'tio-bg-brand-accent text-white'
                  'tio-bg-surface'
                }}"
              {{on "click" (fn this.selectSpecialty specialty)}}
            >
              {{specialty}}
            </Chip>
          {{/each}}
        </div>

        {{! Coach Cards Grid }}
        <DataIndicator
          @source={{this.coachDataSource}}
          @dataSourceId="coach-profiles"
          @label="Coach Profiles"
        >
          <div class="grid md:grid-cols-2 gap-6">
            {{#each this.filteredCoaches as |coach|}}
              <CoachCard @coach={{coach}} @onSchedule={{this.scheduleWithCoach}} />
            {{/each}}
          </div>
        </DataIndicator>

        {{#if (isEqual this.filteredCoaches.length 0)}}
          <div class="text-center py-12">
            <PhQuestion @size={{48}} class="mx-auto tio-fg-disabled mb-4" />
            <Heading @level={{3}} class="tio-fg-secondary mb-2">
              No coaches found
            </Heading>
            <Text class="tio-fg-muted">
              Try adjusting your search or filters
            </Text>
          </div>
        {{/if}}
      </div>

      {{! Session Types }}
      <div class="px-6 py-8">
        <SectionHeader
          @icon={{PhCalendarCheck}}
          @title="Choose Your Session Type"
          @subtitle="Multiple ways to connect with your coach"
          @iconClass="tio-fg-success"
        />

        <div class="grid md:grid-cols-3 gap-4 mt-4">
          {{#each this.sessionTypes as |type|}}
            {{#let type.icon as |TypeIcon|}}
              <div
                class="relative p-6 rounded-xl border tio-border-default tio-bg-surface hover:tio-border-brand transition-all"
              >
                {{#if type.popular}}
                  <span
                    class="absolute -top-2 right-4 px-2 py-0.5 tio-text-xs-medium tio-bg-success tio-fg-inverse rounded-full"
                  >
                    Most Popular
                  </span>
                {{/if}}

                <div class="tio-row-top-loose">
                  <div class="p-3 rounded-xl tio-bg-brand-soft">
                    <TypeIcon @size={{24}} class="tio-fg-brand" />
                  </div>
                  <div>
                    <Heading @level={{4}} class="mb-1">
                      {{type.name}}
                    </Heading>
                    <Text @size="sm" class="tio-fg-secondary">
                      {{type.description}}
                    </Text>
                  </div>
                </div>
              </div>
            {{/let}}
          {{/each}}
        </div>
      </div>

      {{! FAQ Section }}
      <div class="px-6 py-8 tio-bg-sunken dark:bg-gray-900/50">
        <SectionHeader
          @icon={{PhQuestion}}
          @title="Frequently Asked Questions"
          @subtitle="Everything you need to know about coaching"
          @iconClass="tio-fg-info"
        />

        <FAQAccordion @items={{this.faqItems}} class="mt-4 max-w-3xl" />
      </div>

      {{! CTA Section }}
      <div class="m-6 p-8 tio-gradient-brand rounded-2xl tio-fg-inverse text-center">
        <Heading @level={{2}} class="tio-fg-inverse mb-2">
          Ready to Take Control of Your Student Loans?
        </Heading>
        <p class="tio-fg-on-brand mb-6 max-w-2xl mx-auto">
          Our expert coaches have helped thousands of borrowers save money, qualify for forgiveness,
          and reduce financial stress. Your first session is completely free.
        </p>
        <div class="tio-flex-center gap-4">
          <Button @intent="primary" class="tio-bg-surface tio-fg-brand hover:tio-bg-brand-wash">
            <PhCalendarCheck @size={{20}} class="mr-2" />
            Book Your Free Session
          </Button>
          <LinkTo
            @route="authenticated.repaying-student-debt.coaching.schedule"
            class="tio-inline-row gap-2 tio-fg-inverse hover:text-violet-200 font-medium"
          >
            View All Options
            <PhArrowRight @size={{20}} />
          </LinkTo>
        </div>
      </div>
    </VStack>
  </template>
}

// Template helpers
const isEqual = (a: unknown, b: unknown) => a === b;
